"use strict";

import config from './configLoader';
import DomHelper from './Helpers/Dom';
import Utils from './Helpers/Utils';
import Session from './Models/Session';
import ContentService from './Services/Content';
import ElementBinding from './Models/ElementBinding';
import LoanCalculator from './Services/LoanCalculator';
import LoanInformationService from './Services/LoanInformation';
import getProcessorInstance from './Models/PageProcessor/Factory';

export default class Bootstrap {

	constructor(linkId = '', purge = false)
	{
		if (!linkId) {
			throw new Error("linkId is empty, please define it.");
		}

		this.linkId = linkId;
		this.pagesToProcess = {};
		this.loanCalculator = new LoanCalculator(linkId);
		this.contentService = new ContentService(linkId);
		this.loanInformationService = new LoanInformationService(linkId);

		window._sparxpres = window._sparxpres || {};

		this.loadSettings().then(appSettings => {
			this.settings = appSettings;
			this.fetchPageSettings(this.settings);

			if (purge) {
				this.run(true);
			} else {
				this.addReprocessEvents(this.settings.general.renderOnEvent);

				const runNow = !(this.settings.general.hasOwnProperty('onlyRenderOnEvent') && this.settings.general.onlyRenderOnEvent === "1");
				if (runNow) {
					this.run();
				}
			}
		});
	}

	/**
	 * Add reprocess events
	 */
	addReprocessEvents(renderOnEvent = null)
	{
		let self = this;
		window.addEventListener('hashchange', function(event) {
			self.debug("MainPage, hashchange event caught, run now");
			self.fetchPageSettings(self.settings);
		    self.run();
		});

		window.addEventListener('popstate', function (event) {
			self.debug("MainPage, popstate event caught, run now");
			self.fetchPageSettings(self.settings);
			self.run();
		});

		window.addEventListener('sparxpresRender', (event) => {
			self.debug("MainPage, 'sparxpresRender' event caught, run now");
			self.fetchPageSettings(self.settings);
			self.run(true);
		});

		window.addEventListener('showFriBikeSmartWebSale', (event) => {
			self.removeProductPageElement();
			Session.remove('sparxpres-loan-info');
			new Bootstrap('a98b581b-3e10-459a-aaf6-ecb8a4688cb1', true);
		});

		window.addEventListener('showDefaultWebSale', (event) => {
			self.removeProductPageElement();
			Session.remove('sparxpres-loan-info');
			new Bootstrap(self.linkId, true);
		});

		if (renderOnEvent && renderOnEvent.trim().length > 0) {
			self.debug("MainPage, adding '%s' run event", renderOnEvent);
			window.addEventListener(renderOnEvent.trim(), function() {
				self.debug("ProcessPage, '%s' window event caught, running in 500 ms", renderOnEvent);
				Utils.delay(500).then(() => {
					self.fetchPageSettings(self.settings);
					self.run();
				});
			});

			document.addEventListener(renderOnEvent.trim(), function() {
				self.debug("MainPage, '%s' document event caught, running in 500 ms", renderOnEvent);
				Utils.delay(500).then(() => {
					self.fetchPageSettings(self.settings);
					self.run();
				});
			});
		}
	}

	removeProductPageElement() {
		const productEl = document.querySelector(".sparxpres_web_sale.sparxpres__product");
		if (productEl) {
			productEl.remove();
		}
	}

	/**
	 * Start running the module
	 * @param {Boolean} purge
	 * @return
	 */
	run(purge = false)
	{
		let self = this;

		if (self.isInTestMode()) {
			self.startProcessingTestMode();
		} else {
			self.startProcessing(0, purge);
		}
	}

	/**
	 * Check if system is running test mode
	 * @return {Boolean}
	 */
	isInTestMode() 
	{
		return !!+this.settings.general.test;
	}

	/**
	 * Check if system is running debug mode
	 * @returns {Boolean}
	 */
	isInDebugMode() {
		return !!+this.settings.general.debug;
	}

	/**
	 * Process in test mode (will only start processing if spxTest param is set)
	 * @return {Boolean}
	 */
	startProcessingTestMode()
	{
		let self = this;

		Utils.info('!!Test mode is activated!! (set url param: "spxTest=run" to run...)');

		let testParam = self.getParam("spxTest");
		if (testParam && testParam === "run") {
			self.startProcessing();
		}
	}

	/**
	 * Get param from url
	 * return {String}
	 */
	getParam(param)
	{
		return new URLSearchParams(document.location.search).get(param);
	}

	/**
	 * Start page processors
	 * @param {Number} tracking
	 * @param {Boolean} purge
	 * return
	 */
	startProcessing(tracking = 0, purge = false)
	{
		let self = this;
		let enabledProcessors = config("processers.enabled");
		let orderLength = enabledProcessors.length;

		if (orderLength <= tracking) {
			return;
		}

		let currentItem = enabledProcessors[tracking];
		try {
			if (self.pagesToProcess.hasOwnProperty(currentItem)) {
				let pageSettings = self.pagesToProcess[currentItem];
				self.processPage(currentItem, pageSettings, () => {
					// When one page processor has completed, the next one will be started
					self.startProcessing(++tracking, purge);
				}, purge);
			} else {
				self.startProcessing(++tracking, purge);
			}
		} catch (error) {
			Utils.info("A page processor throw an uncaught exception/error", error);
		}
	}

	/**
	 * Load settings from remote service
	 * @return {Promise}
	 */
	 loadSettings()
	 {
	 	let settings;
	 	if (settings = Session.get("sparxpres-settings")) {
	 		return Promise.resolve(settings);
	 	}

	 	throw new Error("Settings weren't found in session");
	 }

	/**
	 * Load settings from remote service
	 * @param {Object} settings
	 * @return {Object|null}
	 */
	 fetchPageSettings(settings)
	 {
	 	let self = this;
	 	let pageSelectors = settings.pageSelectors;
	 	let pages = settings.pages;

	 	for (let key in pageSelectors) {
	 		if (!pageSelectors[key]) {
	 			continue;
	 		}

	 		// Deactivate processor by removing its settings
 			if (self.pagesToProcess[key]) {
 				self.pagesToProcess[key] = false;
 			}

	 		if (self.checkPageSelector(pageSelectors[key])) {
				self.pagesToProcess[key] = pages[key];
 				self.pagesToProcess[key]["general"] = settings.general;
	 		}
	 	}
	 }

	 checkPageSelector(pageSelector)
	 {
	 	let pageSelectorArray = pageSelector.split(",");
	 	let found = false;

 		for (const _pageSelector of pageSelectorArray) {
 		 	let isUrl = /(^https?:)|(^\/)/.test(_pageSelector);
	 		let isUrlMatch = false;

	 		if (isUrl) {
	 			isUrlMatch = location.href.indexOf(_pageSelector) !== -1;
	 		}

	 		if (!isUrlMatch && !DomHelper.grepElementIgnoreErrors(_pageSelector)) {
	 			return false;
 			}

 			found = true;
 		}

 		return found; 
	 }

	/**
	 * Start processing selected page according to relevant settings
	 * @param pageType
	 * @param {Object} pageSettings
	 * @param callback
	 * @param {Boolean} purge
	 * @return
	 */
	 processPage(pageType, pageSettings, callback, purge = false) {
	 	let processor = getProcessorInstance(pageType, purge);
		this.debug("ProcessPage, pageType is: '%s'", pageType);

	 	// If page settings is set to false, it means we should deactivate the processor.
	 	if (pageSettings === false) {
	 		this.debug("ProcessPage returning, because pageSettings is false!");
	 		processor.setActive(false);
	 		callback();
	 		return;
	 	}

	 	processor.setActive(true);
	 	processor.setLinkId(this.linkId);
	 	processor.setDebugMode(this.isInDebugMode());
		processor.setSettings(pageSettings);
	 	processor.setPageType(pageType);

	 	if (typeof processor.setContentService === 'function') {
	 		processor.setContentService(this.contentService);
	 	}

	 	if (typeof processor.setLoanInformationService === 'function') {
	 		processor.setLoanInformationService(this.loanInformationService);
	 	}

	 	if (typeof processor.setLoanCalculatorService === 'function') {
	 		processor.setLoanCalculatorService(this.loanCalculator);
	 	}

	 	if (typeof processor.setElementBinding === 'function') {
	 		if (purge || !processor.getElementBinding()) {
	 			processor.setElementBinding(new ElementBinding(this.settings, this.isInDebugMode()));
	 		}
	 	}

	 	if (purge) {
	 		let eventHandlers = window._sparxpres.eventHandlers;
	 		for(let eventKey in eventHandlers) {
	 			window.removeEventListener(eventKey, eventHandlers[eventKey]);
			}
		}

		// Start processing
		processor.process(callback);
	}


	/**
	 * Log a message if in debug mode
	 * @param msg
	 * @param args
	 */
	debug(msg, ...args) {
		Utils.debug(this.isInDebugMode(), msg, ...args);
	}

}